<template>
  <div class="reward-step" data-test-id="reward-step">
    <v-form ref="form">
      <div class="form-fields">
        <div class="section-header">
          <Typography variant="h-5">Add a budget</Typography>
        </div>

        <v-select
          outlined
          color="brandPrimary"
          label="Budget*"
          :items="budgetOptions"
          :loading="loadingBudgets"
          item-text="text"
          item-value="value"
          hide-details="auto"
          :value="selectedBudgetId"
          :rules="budgetIdRules"
          @input="onBudgetSelected"
        >
          <template #selection="{ item }">
            <div class="budget-option">
              <span>{{ item.name }}</span>
              <span>{{ item.balanceLabel }}</span>
            </div>
          </template>
          <template #item="{ item }">
            <div class="budget-option">
              <span>{{ item.name }}</span>
              <span>{{ item.balanceLabel }}</span>
            </div>
          </template>
        </v-select>

        <div class="payout">
          <Typography variant="h-5">When should it payout?</Typography>

          <v-radio-group
            :value="payoutCadence"
            hide-details="auto"
            class="payout-radio-group"
            :disabled="payoutLocked"
            @change="onPayoutCadenceChange"
          >
            <v-radio value="IMMEDIATE" color="brandPrimary">
              <template #label>
                <div class="radio-label">
                  <Typography variant="body-1 black--text">Immediately</Typography>
                  <Typography variant="caption-2">
                    Rewards are issued as soon as they're earned and approved.
                  </Typography>
                </div>
              </template>
            </v-radio>

            <v-radio value="PROGRAM_END" color="brandPrimary">
              <template #label>
                <div class="radio-label">
                  <Typography variant="body-1 black--text">After program ends</Typography>
                  <Typography variant="caption-2">
                    Rewards are held and issued a set number of days after the program closes.
                  </Typography>
                </div>
              </template>
            </v-radio>

            <div v-if="payoutCadence === 'PROGRAM_END'" class="payout-after">
              <div class="days-row">
                <v-text-field
                  :value="payoutEndOffsetDays"
                  outlined
                  color="brandPrimary"
                  type="number"
                  min="0"
                  aria-label="Days after the program ends"
                  :rules="payoutEndOffsetDaysRules"
                  hide-details
                  class="days-input"
                  :readonly="payoutLocked"
                  :disabled="payoutLocked"
                  @input="onPayoutEndOffsetDaysInput"
                  @update:error="payoutEndOffsetDaysHasError = $event"
                />
                <Typography variant="body-1" class="pt-4">days after the program ends</Typography>
              </div>

              <Alert
                v-if="payoutEndOffsetDaysHasError && payoutEndOffsetDaysError"
                full-width
                variant="error"
                :message="payoutEndOffsetDaysError"
                data-test-id="payout-offset-days-error"
              />

              <div v-if="payoutDateDisplay" class="payout-info">
                <Typography variant="body-2">
                  Rewards will be paid out on <strong>{{ payoutDateDisplay }}</strong> — based on
                  your program end date of {{ programEndDateDisplay }}
                </Typography>
              </div>
            </div>
          </v-radio-group>
        </div>
      </div>
    </v-form>
  </div>
</template>

<script>
import currency from "currency.js";
import moment from "moment";
import Alert from "@/components/generic/Alert.vue";
import Typography from "@/components/generic/Typography.vue";
import {
  clearsRewardCutoffFloor,
  getPayoutDate,
  MIN_REWARD_CUTOFF_MINUTES,
} from "@/utils/payoutDate";

const PAYOUT_INFO_DATE_FORMAT = "MM/DD/YYYY";

export default {
  name: "RewardStep",
  components: {
    Alert,
    Typography,
  },
  props: {
    budgetId: {
      type: Number,
      default: null,
    },
    budgets: {
      type: Array,
      default: () => [],
    },
    loadingBudgets: {
      type: Boolean,
      default: false,
    },
    canSelectBudget: {
      type: Boolean,
      default: false,
    },
    payoutCadence: {
      type: String,
      default: null,
      validator: (v) => v == null || v === "IMMEDIATE" || v === "PROGRAM_END",
    },
    payoutEndOffsetDays: {
      type: Number,
      default: null,
    },
    payoutAmount: {
      type: Number,
      default: 10,
    },
    programEndDate: {
      type: String,
      default: null,
    },
    ruleCalculationWindow: {
      type: String,
      default: null,
    },
    payoutLocked: {
      type: Boolean,
      default: false,
    },
  },
  emits: [
    "update:budgetId",
    "update:isDollarBudget",
    "update:payoutCadence",
    "update:payoutEndOffsetDays",
    "update:payoutAmount",
  ],
  data() {
    return {
      budgetIdRules: [(v) => v != null || "Please select a budget"],
      // Cadence defaults to IMMEDIATE in the parent, so no required-rule is
      // needed — the radio always has a value.
      // The offset days field is only mounted when payoutCadence === "PROGRAM_END"
      // (v-if), so we don't need to conditionally apply these rules — they only
      // fire while the field is visible.
      payoutEndOffsetDaysRules: [
        (v) => (v != null && v !== "") || "Please enter a number of days",
        (v) => v == null || v === "" || Number(v) >= 0 || "Days must be 0 or greater",
        (v) => this.isWithinRewardCutoff(v) || this.rewardCutoffError,
      ],
      payoutEndOffsetDaysHasError: false,
    };
  },
  computed: {
    payoutEndOffsetDaysError() {
      for (const rule of this.payoutEndOffsetDaysRules) {
        const result = rule(this.payoutEndOffsetDays);
        if (typeof result === "string") return result;
      }
      return null;
    },
    rewardCutoffMoment() {
      if (!this.ruleCalculationWindow) return null;
      const m = moment(this.ruleCalculationWindow);
      return m.isValid() ? m : null;
    },
    rewardCutoffDisplay() {
      return this.rewardCutoffMoment?.format(PAYOUT_INFO_DATE_FORMAT) ?? null;
    },
    // This field takes whole days, and the display format carries no time, so naming
    // the latest payout *date* would repeat the cutoff's own date and tell the user
    // nothing. The largest offset that still clears the floor is what they can act on.
    maxPayoutOffsetDays() {
      if (!this.rewardCutoffMoment || !this.programEndDate) return null;
      const end = moment(this.programEndDate);
      if (!end.isValid()) return null;
      const latest = moment(this.rewardCutoffMoment).subtract(MIN_REWARD_CUTOFF_MINUTES, "minutes");
      const days = Math.floor(latest.diff(end, "days", true));
      return days >= 0 ? days : null;
    },
    rewardCutoffError() {
      const clears =
        `Payout must clear your reward cutoff (${this.rewardCutoffDisplay}) by at least ` +
        `${MIN_REWARD_CUTOFF_MINUTES} minutes`;
      // No offset at all can clear it, so the fix is on the step that owns the cutoff.
      if (this.maxPayoutOffsetDays == null)
        return `${clears}. Move the reward cutoff later on the Timeline step.`;
      const days = this.maxPayoutOffsetDays;
      return `${clears} — ${days} ${days === 1 ? "day" : "days"} at most.`;
    },
    selectedBudgetId() {
      return this.budgetId == null ? null : Number(this.budgetId);
    },
    budgetOptions() {
      return this.budgets.map((b) => {
        const name = b.budgetDisplayName || b.budgetName || b.name || `Budget ${b.budgetId}`;
        const balanceLabel =
          b.budgetBalance >= 0
            ? currency(b.budgetBalance).format({
                symbol: b.awardId == 1 ? "$" : "",
              })
            : "";
        return {
          value: Number(b.budgetId),
          name,
          balanceLabel,
          text: `${name} ${balanceLabel}`,
        };
      });
    },
    programEndMoment() {
      if (!this.programEndDate) return null;
      const m = moment(this.programEndDate);
      return m.isValid() ? m : null;
    },
    programEndDateDisplay() {
      return this.programEndMoment?.format(PAYOUT_INFO_DATE_FORMAT) ?? null;
    },
    payoutDateDisplay() {
      return (
        getPayoutDate(this.programEndDate, this.payoutEndOffsetDays)?.format(
          PAYOUT_INFO_DATE_FORMAT
        ) ?? null
      );
    },
  },
  watch: {
    payoutCadence(cadence) {
      if (cadence !== "PROGRAM_END") this.payoutEndOffsetDaysHasError = false;
    },
  },
  methods: {
    onBudgetSelected(budgetId) {
      this.$emit("update:budgetId", budgetId == null ? null : Number(budgetId));
    },
    onPayoutCadenceChange(cadence) {
      if (this.payoutLocked) return;
      // Keep whatever the user had in the offset field — it survives cadence
      // toggles at the step level. The parent's buildPayload strips it back
      // to null on save when the persisted cadence is IMMEDIATE.
      this.$emit("update:payoutCadence", cadence);
    },
    // v-text-field emits a string; coerce to Number for the program record (or
    // null when the user clears the field, so we don't send `NaN` to the API).
    onPayoutEndOffsetDaysInput(value) {
      if (this.payoutLocked) return;
      const trimmed = typeof value === "string" ? value.trim() : value;
      if (trimmed === "" || trimmed == null) {
        this.$emit("update:payoutEndOffsetDays", null);
        return;
      }
      const num = Number(trimmed);
      this.$emit("update:payoutEndOffsetDays", Number.isNaN(num) ? null : num);
    },
    // Skips the check when either date is missing so the other rules own the error.
    // Locked payouts can't be edited here; the Timeline step asks for a later cutoff instead.
    //
    // The same question the Timeline step asks, from the other end: it validates the
    // cutoff against a fixed payout date, this validates the payout offset against a
    // fixed cutoff. Both go through clearsRewardCutoffFloor so the two steps cannot
    // drift -- a day-granular comparison here let an offset through that the Timeline
    // step would reject, and the builder validates only the step you are on.
    isWithinRewardCutoff(days) {
      if (this.payoutLocked) return true;
      if (days === "" || days == null || !this.rewardCutoffMoment) return true;
      return clearsRewardCutoffFloor(
        this.rewardCutoffMoment,
        getPayoutDate(this.programEndDate, Number(days))
      );
    },
    // Delegate to Vuetify — v-form.validate() fires the rules on every mounted
    // input under it, including the offset-days field when it's visible.
    validate() {
      return this.$refs.form?.validate() ?? false;
    },
  },
};
</script>

<style scoped>
.reward-step {
  display: flex;
  flex-direction: column;
  gap: 24px;
}

.form-fields {
  display: flex;
  flex-direction: column;
  gap: 24px;
}

.budget-option {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
}

.step-header {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.section-header {
  display: flex;
  flex-direction: column;
}

.payout {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.payout-radio-group {
  padding-top: 0;
  margin: 0;
}

.radio-label {
  display: flex;
  flex-direction: column;
  padding-top: 8px;
}

.payout-after {
  display: flex;
  flex-direction: column;
  gap: 16px;
  padding-left: 42px;
  padding-top: 8px;
}

.days-row {
  display: flex;
  align-items: start;
  gap: 16px;
}

.days-input {
  width: 96px;
  flex: 0 0 96px;
}

.payout-info {
  background-color: var(--v-surfaceLight-base);
  border: 1px solid var(--v-grey-base);
  padding: 16px;
  border-radius: 4px;
}

.reward-step ::v-deep .v-input--radio-group__input {
  gap: 2px;
}
</style>
